import SoftphoneTabLock, { HANDOVER_JITTER_MAX_MS } from './tabLock'

/**
 * Which tab owns the softphone.
 *
 * The widget lives in the header now, so every dashboard tab boots a candidate owner. Exactly one
 * of them may hold the WebRTC transport - two would both register and both ring for the same agent
 * - and the one that holds it must be allowed to KEEP it: an agent who opens a second tab to read a
 * report cannot have the call in the first tab taken away.
 *
 * The Web Locks API is that guarantee, and is preferred wherever it exists:
 *
 * - The lock is released by the browser when the tab closes, reloads, crashes or is killed for
 *   memory. There is no lease to expire, no heartbeat to miss and no stale holder to time out.
 * - Waiting requests are queued FIFO and granted the instant the holder goes away, which is the
 *   handover: a mirror simply asks for the lock and is answered whenever it falls free.
 * - `navigator.locks.query()` reports who holds and who waits, so "is exactly one tab running the
 *   softphone?" is an answerable question rather than an article of faith. See `describe()`.
 *
 * Browsers with no Web Locks (Safari <15.4, some embedded webviews, any non-secure context) fall
 * back to the BroadcastChannel election in `tabLock.js`, which reaches the same policy with two
 * accepted weaknesses: a tab that CRASHES sends no release, so the lock stays taken until a mirror
 * next asks for it, and a claim racing an unresponsive incumbent is settled by a timeout rather
 * than by the browser.
 */
export const SOFTPHONE_LOCK_NAME = 'ringbax-softphone-owner'

export function isWebLocksSupported() {
  return typeof navigator !== 'undefined'
    && !!navigator.locks
    && typeof navigator.locks.request === 'function'
}

export class SoftphoneOwnership {
  constructor({
    lockName = SOFTPHONE_LOCK_NAME,
    createTabLock = () => new SoftphoneTabLock()
  } = {}) {
    this.lockName = lockName
    this.isOwner = false
    this.usesWebLocks = isWebLocksSupported()

    // Ownership arrived. `isHandover` is false for the tab that took it on boot and true for one
    // that inherited it from a tab that went away - the caller treats the two differently, because
    // an inherited softphone has a campaign to account for and a boot has nothing.
    this.onGranted = null

    // Ownership was taken away, which only ever happens because another tab deliberately took over.
    this.onLost = null

    this._releaseHeld = null
    this._handoverController = null
    this._createTabLock = createTabLock
    this._tabLock = null
    this._isDestroyed = false
  }

  /**
   * Asks for ownership without waiting for it. Resolves true when this tab may run the softphone.
   *
   * A tab that is refused becomes a mirror AND queues for the lock, so it takes over by itself the
   * moment the owner's tab disappears.
   *
   * @returns {Promise<boolean>}
   */
  async request() {
    if (this._isDestroyed || this.isOwner) {
      return this.isOwner
    }

    if (!this.usesWebLocks) {
      return this._requestWithTabLock()
    }

    const isOwner = await this._requestIfAvailable()

    if (!isOwner) {
      this._waitForHandover()
    }

    return isOwner
  }

  /**
   * Takes ownership away from the tab that has it, because the agent asked for the softphone HERE.
   * Deliberate and user-driven: the previous owner drops its transport, which ends any call it was
   * on, so the caller is responsible for not offering this mid-call.
   *
   * @returns {Promise<boolean>}
   */
  async steal() {
    if (this._isDestroyed || this.isOwner) {
      return this.isOwner
    }

    this._abortHandover()

    if (!this.usesWebLocks) {
      this._ensureTabLock().steal()
      this.isOwner = true

      return true
    }

    // `steal` cannot be combined with `ifAvailable`: it is granted at once either way, and the
    // incumbent's own request rejects with an AbortError, which is how it finds out.
    return this._requestLock({ steal: true })
  }

  release() {
    this._abortHandover()

    this.isOwner = false

    if (this._releaseHeld) {
      const releaseHeld = this._releaseHeld

      this._releaseHeld = null

      releaseHeld()
    }

    if (this._tabLock) {
      this._tabLock.release()
    }
  }

  destroy() {
    this._isDestroyed = true

    this.release()

    if (this._tabLock) {
      this._tabLock.destroy()
      this._tabLock = null
    }

    this.onGranted = null
    this.onLost = null
  }

  /**
   * A snapshot of the lock, for diagnostics and for tests that want to assert the invariant rather
   * than infer it. `heldCount` is the answer to "how many tabs are running the softphone?" and is
   * never above 1 while the Web Locks tier is in use.
   *
   * @returns {Promise<{isSupported: boolean, isOwner: boolean, heldCount: number|null, pendingCount: number|null}>}
   */
  async describe() {
    const summary = {
      isSupported: this.usesWebLocks,
      isOwner: this.isOwner,
      heldCount: null,
      pendingCount: null
    }

    if (!this.usesWebLocks || typeof navigator.locks.query !== 'function') {
      return summary
    }

    try {
      const { held = [], pending = [] } = await navigator.locks.query()

      return {
        ...summary,
        heldCount: held.filter(lock => lock.name === this.lockName).length,
        pendingCount: pending.filter(lock => lock.name === this.lockName).length
      }
    } catch (error) {
      console.error('Failed to query the softphone ownership lock', error)

      return summary
    }
  }

  _requestIfAvailable() {
    return this._requestLock({ ifAvailable: true })
  }

  /**
   * One shape for both grant-now modes (`ifAvailable` and `steal`). The lock is held for as long as
   * the callback's promise is unresolved, so the promise this returns must NOT be the request's -
   * that one only settles on release, which is the lifetime of the tab.
   */
  _requestLock(options) {
    return new Promise(resolve => {
      let hasSettled = false

      const settle = isOwner => {
        if (hasSettled) {
          return
        }

        hasSettled = true

        resolve(isOwner)
      }

      navigator.locks
        .request(this.lockName, options, lock => {
          if (!lock || this._isDestroyed) {
            settle(false)

            return undefined
          }

          return this._hold(() => settle(true))
        })
        .then(
          () => settle(false),
          error => {
            this._handleLockEnded(error)
            settle(false)
          }
        )
    })
  }

  /**
   * Queues for the lock and holds it the moment it falls free, which is what turns a mirror into
   * the owner when the owner's tab closes or crashes. Nothing here polls or times out: the browser
   * grants the next request in the queue as part of releasing the lock.
   */
  _waitForHandover() {
    if (this._isDestroyed || this.isOwner || this._handoverController || !this.usesWebLocks) {
      return
    }

    // Abortable so a tab that is unloading, or one that decides to steal instead, leaves no request
    // sitting in the queue.
    const controller = new AbortController()

    this._handoverController = controller

    navigator.locks
      .request(this.lockName, { signal: controller.signal }, lock => {
        if (this._handoverController === controller) {
          this._handoverController = null
        }

        if (!lock || this._isDestroyed) {
          return undefined
        }

        return this._hold(() => this._announceGranted({ isHandover: true }))
      })
      .then(
        () => undefined,
        error => {
          if (this._handoverController === controller) {
            this._handoverController = null
          }

          this._handleLockEnded(error)
        }
      )
  }

  _hold(announce) {
    this.isOwner = true

    announce()

    return new Promise(resolve => {
      this._releaseHeld = resolve
    })
  }

  /**
   * The held lock is gone. An AbortError here is either a takeover by another tab or this tab's own
   * `_abortHandover`; the two are told apart by whether ownership was ever granted.
   */
  _handleLockEnded(error) {
    this._releaseHeld = null

    if (this._isDestroyed || !this.isOwner) {
      return
    }

    this.isOwner = false

    if (error && error.name !== 'AbortError') {
      console.error('The softphone ownership lock failed', error)
    }

    if (this.onLost) {
      this.onLost()
    }

    // The tab that took over can close or crash too, and this tab is the obvious next owner.
    this._waitForHandover()
  }

  _abortHandover() {
    if (!this._handoverController) {
      return
    }

    const controller = this._handoverController

    this._handoverController = null

    controller.abort()
  }

  _announceGranted({ isHandover }) {
    if (this.onGranted) {
      this.onGranted({ isHandover })
    }
  }

  _ensureTabLock() {
    if (this._tabLock) {
      return this._tabLock
    }

    this._tabLock = this._createTabLock()

    this._tabLock.onLost = () => {
      this.isOwner = false

      if (this.onLost) {
        this.onLost()
      }
    }

    this._tabLock.onReleased = () => this._claimAfterRelease()

    return this._tabLock
  }

  async _requestWithTabLock() {
    const isOwner = await this._ensureTabLock().requestClaim()

    this.isOwner = isOwner

    return isOwner
  }

  /**
   * The fallback tier's handover. A release wakes every mirror at once, so each waits a random beat
   * before claiming - otherwise they would all claim together and the tie-break would decide it.
   */
  _claimAfterRelease() {
    if (this._isDestroyed || this.isOwner) {
      return
    }

    setTimeout(async () => {
      if (this._isDestroyed || this.isOwner) {
        return
      }

      const isOwner = await this._ensureTabLock().requestClaim()

      if (!isOwner || this._isDestroyed) {
        return
      }

      this.isOwner = true

      this._announceGranted({ isHandover: true })
    }, Math.random() * HANDOVER_JITTER_MAX_MS)
  }
}

export function createSoftphoneOwnership(options) {
  return new SoftphoneOwnership(options)
}

export default createSoftphoneOwnership
